import { useMemo } from "react";

import { skipToken } from "@reduxjs/toolkit/query";
import { useSelector } from "react-redux";

import { selectProfileSSOid } from "../../app-main/selectors/profile.selectors";
import { Links } from "../../constants/links.consts";
import type {
  MessagesSearchResponse,
  MessagesSearchThread,
} from "../../services/messages.service";
import { useSearchMessagesQuery } from "../../services/messages.service";
import {
  UPCOMING_BOOKINGS_DISPLAY_LIMIT,
  UPCOMING_BOOKINGS_PAGE_SIZE,
} from "../constants/host-hub-upcoming-panel.consts";
import type {
  HostHubUpcomingBooking,
  HostHubUpcomingBookingGroups,
} from "../types/model/upcoming-booking";
import {
  createEmptyUpcomingBookingGroups,
  formatBookingType,
  formatGuestDisplayName,
  getUpcomingBookingsDateRange,
  groupUpcomingBookings,
} from "../utils/host-hub-upcoming-panel.utils";

type UseHostHubUpcomingBookingsResult = {
  bookings: HostHubUpcomingBooking[];
  groups: HostHubUpcomingBookingGroups;
  isError: boolean;
  isLoading: boolean;
};

const findGuest = (thread: MessagesSearchThread, hostSsoId?: string) =>
  thread.participants?.find((participant) => participant.ssoid !== hostSsoId);

const toUpcomingBooking = (
  thread: MessagesSearchThread,
  hostSsoId?: string,
): HostHubUpcomingBooking | null => {
  const booking = thread.thread?.booking;
  const startDate = booking?.starts_at;

  if (!booking?.id || !startDate) return null;

  const guest = findGuest(thread, hostSsoId);
  const guestName = formatGuestDisplayName({
    firstName: guest?.name?.first,
    lastName: guest?.name?.last,
  });

  return {
    avatarAlt: guestName || undefined,
    avatarUrl: guest?.avatar_url,
    bookingType: formatBookingType(thread.listing?.use_type ?? ""),
    endDate: booking.ends_at,
    guestName,
    mediaAlt: thread.listing?.title,
    mediaUrl: thread.listing?.thumbnail?.url_small,
    primaryCtaRoute: `${Links.Inbox}/${booking.id}`,
    startDate,
  };
};

const toUpcomingBookings = (
  response: MessagesSearchResponse | undefined,
  hostSsoId?: string,
) =>
  response?.threads
    ?.map((thread) => toUpcomingBooking(thread, hostSsoId))
    .filter((booking): booking is HostHubUpcomingBooking => Boolean(booking)) ??
  [];

const useHostHubUpcomingBookings = (): UseHostHubUpcomingBookingsResult => {
  const ssoId = useSelector(selectProfileSSOid);
  const dateRange = getUpcomingBookingsDateRange();

  const upcomingBookingsQuery = useSearchMessagesQuery(
    ssoId
      ? {
          booking_state: ["ACTIVE"],
          booking_starts_at_from: dateRange.from,
          booking_starts_at_to: dateRange.to,
          participant_ssoid: ssoId,
          size: UPCOMING_BOOKINGS_PAGE_SIZE,
        }
      : skipToken,
  );

  const bookings = useMemo(
    () => toUpcomingBookings(upcomingBookingsQuery.data, ssoId),
    [ssoId, upcomingBookingsQuery.data],
  );
  const groups = useMemo(
    () =>
      groupUpcomingBookings({
        bookings,
        limit: UPCOMING_BOOKINGS_DISPLAY_LIMIT,
      }),
    [bookings],
  );
  // Cache the empty fallback so the hook returns a stable reference
  // across renders when there are no upcoming bookings.
  const emptyGroups = useMemo(() => createEmptyUpcomingBookingGroups(), []);

  return {
    bookings,
    groups: bookings.length ? groups : emptyGroups,
    isError: upcomingBookingsQuery.isError,
    isLoading: upcomingBookingsQuery.isLoading,
  };
};

export default useHostHubUpcomingBookings;
